import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { PATH_SEPARATOR } from '../query/match-query';
import type { AppSearchAction } from '../types';

export const RECENT_SEARCHES_QUERY_KEY = ['app-search', 'recent'] as const;
export const RECENT_SEARCHES_TTL = 5 * 60 * 1000;
export const RECENT_SEARCHES_LIMIT = 5;

export type RecentSearchItem = {
  id: string;
  label: string;
  labelKey?: string;
  description?: string;
  href?: string;
  searchHref?: string;
  path: string[];
  pathKeys?: string[];
  completion: string;
  alias?: string;
  to?: string;
  searchable?: boolean;
  enterable?: boolean;
};

export type TranslateSearchLabel = (key: string) => string;

export const toRecentSearchItem = (
  action: AppSearchAction,
): RecentSearchItem => ({
  id: action.id,
  label: action.label,
  labelKey: action.labelKey,
  description: action.description,
  href: action.href,
  searchHref: action.searchHref,
  path: action.path,
  pathKeys: action.pathKeys,
  completion: action.completion,
  alias: action.alias,
  to: action.to,
  searchable: action.searchable,
  enterable: action.enterable,
});

export const recentSearchBreadcrumb = (path: string[]) =>
  path.join(PATH_SEPARATOR);

export const resolveRecentPath = (
  item: Pick<RecentSearchItem, 'path' | 'pathKeys'>,
  t: TranslateSearchLabel,
) => {
  if (!item.pathKeys?.length) {
    return item.path;
  }

  return [...item.pathKeys.map(t), ...item.path.slice(item.pathKeys.length)];
};

export const toRecentSearchAction = (
  item: RecentSearchItem,
  t: TranslateSearchLabel = (key) => key,
): AppSearchAction & { depth: number } => {
  const path = resolveRecentPath(item, t);
  const label = item.labelKey ? t(item.labelKey) : item.label;
  const breadcrumb = recentSearchBreadcrumb(path);

  return {
    ...item,
    label,
    path,
    completion: breadcrumb,
    description:
      breadcrumb && breadcrumb !== label ? breadcrumb : item.description,
    depth: 0,
    onSelect: () => undefined,
  };
};

export const pushRecentSearch = (
  items: RecentSearchItem[],
  item: RecentSearchItem,
) =>
  [item, ...items.filter((entry) => entry.id !== item.id)].slice(
    0,
    RECENT_SEARCHES_LIMIT,
  );

export const removeRecentSearch = (items: RecentSearchItem[], id: string) =>
  items.filter((entry) => entry.id !== id);

export const useRecentSearches = () => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: RECENT_SEARCHES_QUERY_KEY,
    queryFn: async (): Promise<RecentSearchItem[]> => [],
    staleTime: RECENT_SEARCHES_TTL,
    cacheTime: RECENT_SEARCHES_TTL,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const add = useCallback(
    (action: AppSearchAction) => {
      if (!action.href && !action.searchHref) {
        return;
      }

      queryClient.setQueryData<RecentSearchItem[]>(
        RECENT_SEARCHES_QUERY_KEY,
        (prev = []) => pushRecentSearch(prev, toRecentSearchItem(action)),
      );
    },
    [queryClient],
  );

  const remove = useCallback(
    (id: string) => {
      queryClient.setQueryData<RecentSearchItem[]>(
        RECENT_SEARCHES_QUERY_KEY,
        (prev = []) => removeRecentSearch(prev, id),
      );
    },
    [queryClient],
  );

  return { items: query.data ?? [], add, remove };
};